import React from 'react';
import Icon from '../../../components/AppIcon';

// ---------------------------------------------------------------------------
// PatternsList — cross-student patterns from analyzeCaseload().patterns.
//
// The analytics library labels each pattern with a `kind` (function_cluster,
// stalled_goals, regressing_goals, compliance, ahead, critical_behavior)
// and pre-computed evidence counts. This component just renders them.
//
// Each pattern gets a color tone that matches its urgency, and a "who is
// this about?" line that resolves the studentIds into student names if a
// resolver is available.
// ---------------------------------------------------------------------------

const KIND_TONE = {
  critical_behavior: { border: 'border-red-200', bg: 'bg-red-50', text: 'text-red-800', icon: 'text-red-600' },
  compliance: { border: 'border-red-200', bg: 'bg-red-50', text: 'text-red-800', icon: 'text-red-600' },
  regressing_goals: { border: 'border-amber-200', bg: 'bg-amber-50', text: 'text-amber-800', icon: 'text-amber-600' },
  stalled_goals: { border: 'border-amber-200', bg: 'bg-amber-50', text: 'text-amber-800', icon: 'text-amber-600' },
  function_cluster: { border: 'border-indigo-200', bg: 'bg-indigo-50', text: 'text-indigo-800', icon: 'text-indigo-600' },
  ahead: { border: 'border-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-800', icon: 'text-emerald-600' },
};

const PatternsList = ({ patterns = [], resolveStudentNames, onOpenStudent }) => {
  if (!patterns || patterns.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-6 text-center">
        <Icon name="Sparkles" size={20} className="text-slate-400 mx-auto mb-2" />
        <p className="text-sm text-slate-500">
          No caseload-wide patterns yet. Log a few more incidents and progress
          entries and this section will start highlighting shared trends.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {patterns.map((p) => {
        const tone = KIND_TONE[p.kind] || KIND_TONE.function_cluster;
        const names = resolveStudentNames ? resolveStudentNames(p.studentIds) : [];
        return (
          <div key={p.id} className={`rounded-lg border ${tone.border} ${tone.bg} p-4`}>
            <div className="flex items-start gap-3">
              <div className={`mt-0.5 ${tone.icon}`}>
                <Icon name={p.icon || 'Sparkles'} size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className={`font-semibold text-sm ${tone.text}`}>{p.headline}</h4>
                <p className="text-sm text-slate-700 mt-1">{p.detail}</p>
                {names.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {names.map((n, idx) => (
                      <button
                        key={`${p.id}-${idx}`}
                        type="button"
                        onClick={() => onOpenStudent && onOpenStudent(p.studentIds[idx])}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/70 border border-slate-200 text-xs text-slate-700 hover:bg-white hover:border-slate-300"
                      >
                        <Icon name="User" size={11} />
                        {n}
                      </button>
                    ))}
                  </div>
                )}
                <div className="mt-2 text-xs text-slate-500">
                  Evidence: {p.evidenceCount} data point{p.evidenceCount === 1 ? '' : 's'}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PatternsList;
